import type { SaunaSessionSummary, SaunaSetupNoteView } from '@jums-dashboard/shared';
import type { ChartData, ChartOptions, Plugin, TooltipItem } from 'chart.js';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import { buildHeatingRatePoints, buildSetupNoteMarkers, formatHeatingRate, formatShortDate } from './sessionHistory';

const RATE_COLOR = 'rgb(99, 102, 241)';
const NOTE_COLOR = 'rgb(217, 119, 6)';
const AXIS_COLOR = 'rgb(156, 163, 175)';
const DAY_MS = 86_400_000;

export interface SaunaHeatingRateChartProps {
  sessions: readonly SaunaSessionSummary[];
  setupNotes: readonly SaunaSetupNoteView[];
  windowDays: number;
  generatedAt: string;
}

type Point = { x: number; y: number };

const NOTE_DATASET_INDEX = 1;

/** Draws a dashed line under each setup-note marker; reads the chart's current data so updates are picked up. */
const markerLinesPlugin: Plugin<'line'> = {
  id: 'saunaSetupNoteLines',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const notePoints = (chart.data.datasets[NOTE_DATASET_INDEX]?.data ?? []) as Point[];
    ctx.save();
    ctx.strokeStyle = NOTE_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    for (const p of notePoints) {
      const px = scales.x.getPixelForValue(p.x);
      if (px < chartArea.left || px > chartArea.right) continue;
      ctx.beginPath();
      ctx.moveTo(px, chartArea.top);
      ctx.lineTo(px, chartArea.bottom);
      ctx.stroke();
    }
    ctx.restore();
  },
};
const CHART_PLUGINS = [markerLinesPlugin];

/** Heating rate per completed session over time, with setup notes as dashed vertical markers. */
export default function SaunaHeatingRateChart({ sessions, setupNotes, windowDays, generatedAt }: SaunaHeatingRateChartProps) {
  const points = useMemo(() => buildHeatingRatePoints(sessions), [sessions]);

  const maxX = useMemo(() => {
    const ms = Date.parse(generatedAt);
    return Number.isFinite(ms) ? ms : Date.now();
  }, [generatedAt]);
  const minX = maxX - windowDays * DAY_MS;

  const markers = useMemo(() => buildSetupNoteMarkers(setupNotes, minX, maxX), [setupNotes, minX, maxX]);
  const markerY = useMemo(() => Math.max(1, ...points.map((p) => p.y)) * 1.1, [points]);

  const data = useMemo<ChartData<'line', Point[]>>(
    () => ({
      datasets: [
        {
          label: 'Heating rate',
          data: points.map((p) => ({ x: p.x, y: p.y })),
          borderColor: RATE_COLOR,
          pointBackgroundColor: points.map((p) => (p.lowConfidence ? 'rgba(0,0,0,0)' : RATE_COLOR)),
          pointBorderColor: RATE_COLOR,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2,
          tension: 0.2,
        },
        {
          label: 'Setup notes',
          data: markers.map((m) => ({ x: m.x, y: markerY })),
          showLine: false,
          pointStyle: 'triangle',
          rotation: 180,
          pointRadius: 6,
          pointHoverRadius: 8,
          borderColor: NOTE_COLOR,
          backgroundColor: NOTE_COLOR,
        },
      ],
    }),
    [points, markers, markerY],
  );

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<'line'>[]) => (items[0] ? formatShortDate(items[0].parsed.x ?? 0) : ''),
            label: (ctx: TooltipItem<'line'>) => {
              if (ctx.datasetIndex === NOTE_DATASET_INDEX) return markers[ctx.dataIndex]?.text ?? '';
              const p = points[ctx.dataIndex];
              return p ? `${formatHeatingRate(p.y)}${p.lowConfidence ? ' (low confidence)' : ''}` : '';
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: minX,
          max: maxX,
          ticks: { maxTicksLimit: 6, color: AXIS_COLOR, callback: (value) => formatShortDate(Number(value)) },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          suggestedMax: markerY,
          title: { display: true, text: '°C/min', color: AXIS_COLOR },
          ticks: { color: AXIS_COLOR },
          grid: { color: 'rgba(127,127,127,0.12)' },
        },
      },
    }),
    [points, markers, markerY, minX, maxX],
  );

  if (points.length === 0) {
    return <p className="text-sm text-(--text-tertiary)">No completed sessions with a heating rate in the last {windowDays} days.</p>;
  }

  return (
    <figure className="flex flex-col gap-1">
      <div
        className="h-48 w-full"
        role="img"
        aria-label={`Heating rate per completed session over the last ${windowDays} days: ${points.length} sessions, ${markers.length} setup notes marked.`}
      >
        <Line data={data} options={options} plugins={CHART_PLUGINS} />
      </div>
      <figcaption className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-(--text-tertiary)">
        <span>
          <span aria-hidden="true" style={{ color: RATE_COLOR }}>
            ●
          </span>{' '}
          Heating rate (hollow = low confidence)
        </span>
        <span>
          <span aria-hidden="true" style={{ color: NOTE_COLOR }}>
            ▼
          </span>{' '}
          Setup note
        </span>
      </figcaption>
    </figure>
  );
}
